import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Icons
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import GroupIcon from "@mui/icons-material/Group";
import BusinessIcon from "@mui/icons-material/Business";
import SupportIcon from "@mui/icons-material/Support";
import BuildIcon from "@mui/icons-material/Build";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OnlineIcon from "@mui/icons-material/FiberManualRecord";

// Chat components
import ChatRoomList from "./components/ChatRoomList";
import ChatWindow from "./components/ChatWindow";
import CreateRoomModal from "./components/CreateRoomModal";
import VendorNotificationPanel from "./components/VendorNotificationPanel";

// Services
import { chatService } from "../../services/chatService";
import { useWebSocket } from "../../hooks/useWebSocket";

function Chat() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  
  // State management
  const [chatRooms, setChatRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  
  // Menu states
  const [roomMenuAnchor, setRoomMenuAnchor] = useState(null);
  const [messageMenuAnchor, setMessageMenuAnchor] = useState(null);
  
  // WebSocket connection
  const { socket, isConnected, sendMessage } = useWebSocket();
  
  // Refs
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Effects
  useEffect(() => {
    loadChatRooms();
  }, []);
  
  useEffect(() => {
    // Get room ID from URL params
    const params = new URLSearchParams(location.search);
    const roomId = params.get('room');
    if (roomId && chatRooms.length > 0) {
      const room = chatRooms.find(r => r.id === parseInt(roomId));
      if (room) {
        setSelectedRoom(room);
        setShowMobileChat(true);
      }
    }
  }, [location.search, chatRooms]);
  
  useEffect(() => {
    if (selectedRoom) {
      loadMessages(selectedRoom.id);
    }
  }, [selectedRoom]);
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // WebSocket event handlers
  useEffect(() => {
    if (socket) {
      socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
      };
    }
  }, [socket, selectedRoom]);
  
  // Functions
  const loadChatRooms = async () => {
    try {
      setLoading(true);
      const rooms = await chatService.getChatRooms();
      setChatRooms(rooms);
    } catch (error) {
      console.error('Failed to load chat rooms:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadMessages = async (roomId) => {
    try {
      const roomMessages = await chatService.getMessages(roomId);
      setMessages(roomMessages);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };
  
  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'message':
        if (selectedRoom && data.data.chat_room_id === selectedRoom.id) {
          setMessages(prev => [...prev, data.data]);
        }
        // Update room's last message
        setChatRooms(prev => prev.map(room => 
          room.id === data.data.chat_room_id 
            ? { ...room, last_message: data.data, unread_count: room.unread_count + 1 }
            : room
        ));
        break;
        
      case 'typing':
        // Handle typing indicators
        break;
        
      case 'user_joined':
        setOnlineUsers(prev => new Set([...prev, data.data.user_id]));
        break;
        
      case 'user_left':
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(data.data.user_id);
          return newSet;
        });
        break;
        
      case 'message_reaction':
        setMessages(prev => prev.map(msg => 
          msg.id === data.data.message_id 
            ? { ...msg, reactions: [...(msg.reactions || []), data.data] }
            : msg
        ));
        break;
        
      case 'notification':
        // Handle system notifications
        break;
    }
  };
  
  const handleRoomSelect = (room) => {
    setSelectedRoom(room);
    setShowMobileChat(true);
    
    // Update URL
    navigate(`/chat?room=${room.id}`, { replace: true });
    
    // Mark messages as read
    chatService.markRoomAsRead(room.id);
    
    // Update unread count
    setChatRooms(prev => prev.map(r => 
      r.id === room.id ? { ...r, unread_count: 0 } : r
    ));
  };
  
  const handleSendMessage = async (content, messageType = 'text', file = null) => {
    if (!selectedRoom || (!content.trim() && !file)) return;
    
    try {
      let message;
      if (file) {
        message = await chatService.sendFileMessage(selectedRoom.id, content, file);
      } else {
        message = await chatService.sendMessage(selectedRoom.id, content, messageType);
      }
      
      // Add to local state immediately for better UX
      setMessages(prev => [...prev, message]);
      
      // Send via WebSocket for real-time updates
      sendMessage({
        type: 'message',
        room_id: selectedRoom.id,
        data: message
      });
      
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };
  
  const handleCreateRoom = async (roomData) => {
    try {
      const newRoom = await chatService.createChatRoom(roomData);
      setChatRooms(prev => [newRoom, ...prev]);
      setShowCreateRoom(false);
      setSelectedRoom(newRoom);
      setShowMobileChat(true);
    } catch (error) {
      console.error('Failed to create room:', error);
    }
  };
  
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleSendMessage(`Shared ${file.name}`, 'file', file);
    }
  };
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  const filteredRooms = chatRooms.filter(room =>
    room.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    room.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const getChatTypeIcon = (chatType) => {
    switch (chatType) {
      case 'group': return <GroupIcon />;
      case 'support': return <SupportIcon />;
      case 'vendor': return <BuildIcon />;
      case 'property': return <BusinessIcon />;
      default: return <GroupIcon />;
    }
  };
  
  const formatLastMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString();
  };
  
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Grid container spacing={3} sx={{ height: 'calc(100vh - 200px)' }}>
          {/* Chat Room List - Hidden on mobile when chat is open */}
          <Grid 
            item 
            xs={12} 
            md={4} 
            lg={3} 
            sx={{ 
              display: isMobile && showMobileChat ? 'none' : 'block',
              height: '100%'
            }}
          >
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Header */}
              <ArgonBox p={2} borderBottom="1px solid #e0e0e0">
                <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <ArgonTypography variant="h6" fontWeight="bold">
                    Messages
                  </ArgonTypography>
                  <ArgonButton
                    variant="text"
                    color="info"
                    size="small"
                    onClick={() => setShowCreateRoom(true)}
                    startIcon={<AddIcon />}
                  >
                    New
                  </ArgonButton>
                </ArgonBox>
                
                {/* Search */}
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </ArgonBox>
              
              {/* Room List */}
              <ArgonBox sx={{ flex: 1, overflow: 'auto' }}>
                <ChatRoomList
                  rooms={filteredRooms}
                  selectedRoom={selectedRoom}
                  onRoomSelect={handleRoomSelect}
                  onlineUsers={onlineUsers}
                  loading={loading}
                />
              </ArgonBox>
            </Card>
          </Grid>
          
          {/* Chat Window */}
          <Grid 
            item 
            xs={12} 
            md={8} 
            lg={9}
            sx={{ 
              display: isMobile && !showMobileChat ? 'none' : 'block',
              height: '100%'
            }}
          >
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {selectedRoom ? (
                <>
                  {/* Chat Header */}
                  <ArgonBox 
                    p={2} 
                    borderBottom="1px solid #e0e0e0"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <ArgonBox display="flex" alignItems="center">
                      {isMobile && (
                        <IconButton 
                          onClick={() => setShowMobileChat(false)}
                          sx={{ mr: 1 }}
                        >
                          <ArrowBackIcon />
                        </IconButton>
                      )}
                      
                      <Avatar sx={{ mr: 2 }}>
                        {getChatTypeIcon(selectedRoom.chat_type)}
                      </Avatar>
                      
                      <ArgonBox>
                        <ArgonTypography variant="h6" fontWeight="bold">
                          {selectedRoom.name}
                        </ArgonTypography>
                        <ArgonBox display="flex" alignItems="center">
                          <ArgonTypography variant="caption" color="text">
                            {selectedRoom.participants?.length} members
                          </ArgonTypography>
                          {selectedRoom.chat_type === 'vendor' && (
                            <>
                              <Divider orientation="vertical" sx={{ mx: 1, height: 12 }} />
                              <OnlineIcon sx={{ fontSize: 8, color: 'success.main', mr: 0.5 }} />
                              <ArgonTypography variant="caption" color="success">
                                Available
                              </ArgonTypography>
                            </>
                          )}
                        </ArgonBox>
                      </ArgonBox>
                    </ArgonBox>
                    
                    <IconButton onClick={(e) => setRoomMenuAnchor(e.currentTarget)}>
                      <MoreVertIcon />
                    </IconButton>
                  </ArgonBox>
                  
                  {/* Chat Messages */}
                  <ChatWindow
                    room={selectedRoom}
                    messages={messages}
                    onSendMessage={handleSendMessage}
                    onFileUpload={handleFileUpload}
                    messagesEndRef={messagesEndRef}
                    onlineUsers={onlineUsers}
                  />
                </>
              ) : (
                /* No Room Selected */
                <ArgonBox 
                  display="flex" 
                  flexDirection="column" 
                  alignItems="center" 
                  justifyContent="center"
                  height="100%"
                  p={3}
                >
                  <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <ArgonTypography variant="h5" color="text" textAlign="center" mb={1}>
                    Welcome to PRM Chat
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="text" textAlign="center" mb={3}>
                    Select a conversation to start messaging or create a new room
                  </ArgonTypography>
                  <ArgonButton
                    variant="gradient"
                    color="info"
                    onClick={() => setShowCreateRoom(true)}
                    startIcon={<AddIcon />}
                  >
                    Start New Conversation
                  </ArgonButton>
                </ArgonBox>
              )}
            </Card>
          </Grid>
        </Grid>
        
        {/* Vendor Notification Panel - Only for vendor users */}
        <VendorNotificationPanel />
        
        {/* Create Room Modal */}
        <CreateRoomModal
          open={showCreateRoom}
          onClose={() => setShowCreateRoom(false)}
          onCreateRoom={handleCreateRoom}
        />
        
        {/* Room Menu */}
        <Menu
          anchorEl={roomMenuAnchor}
          open={Boolean(roomMenuAnchor)}
          onClose={() => setRoomMenuAnchor(null)}
        >
          <MenuItem onClick={() => setRoomMenuAnchor(null)}>Room Info</MenuItem>
          <MenuItem onClick={() => setRoomMenuAnchor(null)}>Add Members</MenuItem>
          <MenuItem onClick={() => setRoomMenuAnchor(null)}>Notifications</MenuItem>
          <Divider />
          <MenuItem onClick={() => setRoomMenuAnchor(null)} sx={{ color: 'error.main' }}>
            Leave Room
          </MenuItem>
        </Menu>
        
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          multiple
        />
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Chat;